import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AddressBlock from './AddressBlock';
import cartIcon from '../assets/корзина-кнопка.png';
import addedToFavoritesIcon from '../assets/добавленно в избр.png';
import favoritesBlueIcon from '../assets/в избранное-синие.png';
import arrowLeftIcon from '../assets/туда.png';
import sellersPlaceholderIcon from '../assets/продавцы.png';
import { apiClient } from '../api/client';
import type { Product, ProductSpecifications, SellerProfile } from '../api/types';
import { getProductImageUrl } from '../utils/imageUrl';

interface ProductDetailProps {
  productId?: string;
  userId?: string;
  onLogoClick?: () => void;
  onCatalogToggle?: () => void;
  isCatalogOpen?: boolean;
  onFavoritesClick?: () => void;
  onCartClick?: () => void;
  onBack?: () => void;
}

export default function ProductDetail({
  productId,
  userId,
}: ProductDetailProps) {
  const thumbnailsContainerRef = useRef<HTMLDivElement>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [cartQuantity, setCartQuantity] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [product, setProduct] = useState<Product | null>(null);
  const [seller, setSeller] = useState<SellerProfile | null>(null);
  const [specifications, setSpecifications] = useState<ProductSpecifications | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canScrollThumbnailsLeft, setCanScrollThumbnailsLeft] = useState(false);
  const [canScrollThumbnailsRight, setCanScrollThumbnailsRight] = useState(false);

  const fallbackMainImage = 'https://www.figma.com/api/mcp/asset/7630774d-6e9d-4a12-b224-517674161069';
  const fallbackAdditionalImage = 'https://www.figma.com/api/mcp/asset/ed5e747f-2f33-4f51-92df-e2af73d8b16b';
  const updateThumbnailsScrollState = useCallback(() => {
    if (!thumbnailsContainerRef.current) return;

    const container = thumbnailsContainerRef.current;
    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const hasOverflow = container.scrollWidth > container.clientWidth + 1;

    if (!hasOverflow) {
      setCanScrollThumbnailsLeft(false);
      setCanScrollThumbnailsRight(false);
      return;
    }

    setCanScrollThumbnailsLeft(container.scrollLeft > 2);
    setCanScrollThumbnailsRight(container.scrollLeft < maxScrollLeft - 2);
  }, []);

  useEffect(() => {
    const loadProductData = async () => {
      if (!productId) {
        setIsLoading(false);
        setError('Product ID is required');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const productResponse = await apiClient.getProduct(productId);
        if (!productResponse.success || !productResponse.data) {
          throw new Error(productResponse.error || 'Failed to load product');
        }

        const loadedProduct = productResponse.data;
        setProduct(loadedProduct);

        if (loadedProduct.seller_id) {
          try {
            const sellerResponse = await apiClient.getSeller(loadedProduct.seller_id);
            if (sellerResponse.success && sellerResponse.data) {
              setSeller(sellerResponse.data);
            } else {
              setSeller(null);
            }
          } catch (sellerError) {
            console.error('Error loading seller:', sellerError);
            setSeller(null);
          }
        } else {
          setSeller(null);
        }

        const specsResponse = await apiClient.getProductSpecifications(productId);
        if (specsResponse.success && specsResponse.data) {
          setSpecifications(specsResponse.data);
        } else {
          setSpecifications(null);
        }

        if (userId) {
          try {
            const favoritesResponse = await apiClient.getUserFavorites(userId);
            if (favoritesResponse.success && favoritesResponse.data) {
              const favoriteIds = Array.isArray(favoritesResponse.data) ? favoritesResponse.data : [];
              setIsFavorite(favoriteIds.includes(productId));
            } else {
              setIsFavorite(false);
            }
          } catch {
            setIsFavorite(false);
          }

          try {
            const cartResponse = await apiClient.getUserCart(userId);
            if (cartResponse.success && cartResponse.data) {
              const cartItem = (cartResponse.data.items || []).find((item) => item.product_id === productId);
              setCartQuantity(cartItem?.quantity || 0);
            } else {
              setCartQuantity(0);
            }
          } catch {
            setCartQuantity(0);
          }
        } else {
          setIsFavorite(false);
          setCartQuantity(0);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
        console.error('Error loading product:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadProductData();
  }, [productId, userId]);

  const thumbnails = useMemo(() => {
    if (!product) return [] as string[];

    if (product.images?.length) {
      return product.images.map((img) => getProductImageUrl(img.image_url || img.thumbnail_url, fallbackMainImage));
    }

    return [getProductImageUrl(product.primary_image || product.primary_thumbnail, fallbackMainImage)];
  }, [product]);

  const mainImage = thumbnails[currentImageIndex] || getProductImageUrl(product?.primary_image || product?.primary_thumbnail, fallbackMainImage);
  const additionalImage = thumbnails[thumbnails.length - 1] || getProductImageUrl(product?.primary_image, fallbackAdditionalImage);
  const sellerTitle =
    seller?.shop_name ||
    seller?.display_name ||
    [seller?.first_name, seller?.last_name].filter(Boolean).join(' ') ||
    'Магазин';
  const sellerDescription = seller?.description || 'Описание магазина пока не заполнено';
  const sellerAvatar = getProductImageUrl(seller?.avatar_url, sellersPlaceholderIcon);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product?.id]);

  useEffect(() => {
    updateThumbnailsScrollState();
    const container = thumbnailsContainerRef.current;
    container?.addEventListener('scroll', updateThumbnailsScrollState, { passive: true });
    window.addEventListener('resize', updateThumbnailsScrollState);

    const resizeObserver = typeof ResizeObserver !== 'undefined' && container
      ? new ResizeObserver(() => updateThumbnailsScrollState())
      : null;

    if (container && resizeObserver) {
      resizeObserver.observe(container);
    }

    const timeoutId = window.setTimeout(updateThumbnailsScrollState, 150);

    return () => {
      window.clearTimeout(timeoutId);
      container?.removeEventListener('scroll', updateThumbnailsScrollState);
      window.removeEventListener('resize', updateThumbnailsScrollState);
      resizeObserver?.disconnect();
    };
  }, [thumbnails.length, product?.id, updateThumbnailsScrollState]);

  const formatPrice = (price: number) => new Intl.NumberFormat('ru-RU').format(price);

  const discountPercent = (() => {
    if (typeof product?.discount_percent === 'number' && product.discount_percent > 0) {
      return Math.min(90, Math.max(0, product.discount_percent));
    }
    if (product?.old_price && product.old_price > product.price && product.old_price > 0) {
      const pct = Math.round(((product.old_price - product.price) / product.old_price) * 100);
      return Math.min(90, Math.max(0, pct));
    }
    return 0;
  })();

  const inferredOldPrice = (() => {
    if (product?.old_price && product.old_price > product.price) return product.old_price;
    if (discountPercent > 0 && product) {
      const computed = Math.round(product.price / (1 - discountPercent / 100));
      return computed > product.price ? computed : undefined;
    }
    return undefined;
  })();

  const priceBadgeBgClass = (() => {
    if (discountPercent === 0) return 'bg-white';
    if (discountPercent < 20) return 'bg-[#e8f2ff]';
    if (discountPercent < 40) return 'bg-[#e5f8ec]';
    if (discountPercent < 60) return 'bg-[#fff5d8]';
    if (discountPercent < 80) return 'bg-[#ffe9d1]';
    return 'bg-[#ffd8d8]';
  })();

  const handleToggleFavorite = async () => {
    if (!userId || !productId) {
      alert('Необходимо войти в систему');
      return;
    }

    try {
      if (isFavorite) {
        const response = await apiClient.removeFromFavorites(userId, productId);
        if (!response.success) throw new Error(response.error || 'Failed to remove from favorites');
        setIsFavorite(false);
      } else {
        const response = await apiClient.addToFavorites(userId, productId);
        if (!response.success) throw new Error(response.error || 'Failed to add to favorites');
        setIsFavorite(true);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      alert(err instanceof Error ? err.message : 'Не удалось изменить статус избранного');
    }
  };

  const handleAddToCart = async () => {
    if (!userId || !productId) {
      alert('Необходимо войти в систему');
      return;
    }

    try {
      const response = await apiClient.addToCart(userId, productId, 1);
      if (!response.success) throw new Error(response.error || 'Failed to add to cart');
      setCartQuantity(1);
    } catch (err) {
      console.error('Error adding to cart:', err);
      alert(err instanceof Error ? err.message : 'Не удалось добавить в корзину');
    }
  };

  const handleIncreaseCart = async () => {
    if (!userId || !productId) return;

    try {
      const nextQty = cartQuantity + 1;
      const response = await apiClient.updateCartItem(userId, productId, nextQty);
      if (!response.success) throw new Error(response.error || 'Failed to update cart');
      setCartQuantity(nextQty);
    } catch (err) {
      console.error('Error increasing cart quantity:', err);
      alert(err instanceof Error ? err.message : 'Не удалось изменить количество');
    }
  };

  const handleDecreaseCart = async () => {
    if (!userId || !productId || cartQuantity <= 0) return;

    try {
      if (cartQuantity === 1) {
        const response = await apiClient.removeFromCart(userId, productId);
        if (!response.success) throw new Error(response.error || 'Failed to remove from cart');
        setCartQuantity(0);
        return;
      }

      const nextQty = cartQuantity - 1;
      const response = await apiClient.updateCartItem(userId, productId, nextQty);
      if (!response.success) throw new Error(response.error || 'Failed to update cart');
      setCartQuantity(nextQty);
    } catch (err) {
      console.error('Error decreasing cart quantity:', err);
      alert(err instanceof Error ? err.message : 'Не удалось изменить количество');
    }
  };

  const scrollThumbs = (direction: 'left' | 'right') => {
    if (!thumbnailsContainerRef.current) return;
    const step = window.innerWidth < 640 ? 184 : 256;
    thumbnailsContainerRef.current.scrollBy({
      left: direction === 'left' ? -step : step,
      behavior: 'smooth',
    });
  };

  return (
    <div className="bg-[#f3f3f3] min-h-screen">
      {isLoading ? (
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-[20px] text-gray-600">Загрузка товара...</div>
        </div>
      ) : error ? (
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-[20px] text-red-600">Ошибка загрузки: {error}</div>
        </div>
      ) : !product ? (
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-[20px] text-gray-600">Товар не найден</div>
        </div>
      ) : (
        <main className="mx-auto w-full max-w-[1520px] px-4 pb-10 pt-5 sm:px-6 lg:px-8">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_360px]">
            <section className="order-1 rounded-[24px] bg-white px-5 py-5 shadow-[0_8px_24px_rgba(0,0,0,0.06)] sm:px-6 sm:py-6 lg:px-7 lg:py-7 xl:px-8 xl:py-8">
              <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)] xl:items-start xl:gap-8">
                <div className="order-2 xl:order-1">
                  <h1
                    className="text-[28px] font-semibold leading-[1.08] text-black sm:text-[34px] lg:text-[42px]"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {product.name}
                  </h1>

                  <div className="mt-8">
                    <h2 className="mb-5 text-[24px] font-semibold leading-none text-black sm:text-[28px]">Характеристики</h2>
                    {specifications && Object.keys(specifications).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(specifications).map(([key, values]) => (
                          <div key={key} className="grid grid-cols-[minmax(96px,140px)_1fr] gap-x-4 gap-y-1 sm:grid-cols-[minmax(110px,160px)_1fr] sm:gap-x-5">
                            <div className="text-[15px] leading-[22px] text-[#727272] sm:text-[16px]">{key}</div>
                            <div className="space-y-1 text-[15px] leading-[22px] text-black sm:text-[16px]">
                              {values.map((spec, idx) => (
                                <div key={idx}>{spec.value}</div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-[15px] text-[#727272]">
                        Характеристики не указаны
                      </div>
                    )}
                  </div>
                </div>

                <div className="order-1 xl:order-2 min-w-0">
                  <div className="overflow-hidden rounded-[24px] bg-white shadow-[inset_0_0_0_1px_rgba(93,118,203,0.08)]">
                    <div className="aspect-[4/5] bg-white p-2 sm:p-4 lg:p-5">
                      <img src={mainImage} alt={product.name} className="h-full w-full object-contain" />
                    </div>
                  </div>

                  <div className="relative mt-5">
                    {canScrollThumbnailsLeft && (
                      <button
                        type="button"
                        onClick={() => scrollThumbs('left')}
                        className="absolute left-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 shadow-[0_4px_10px_rgba(0,0,0,0.12)]"
                        aria-label="Прокрутить миниатюры влево"
                      >
                        <img src={arrowLeftIcon} alt="Назад" className="h-4 w-4 rotate-180" />
                      </button>
                    )}

                    <div ref={thumbnailsContainerRef} className="scrollbar-hide flex gap-2 overflow-x-auto px-1 py-1 sm:gap-4">
                      {thumbnails.map((thumb, index) => (
                        <button
                          key={`${thumb}-${index}`}
                          type="button"
                          onClick={() => setCurrentImageIndex(index)}
                          className={`h-[84px] w-[84px] flex-shrink-0 overflow-hidden rounded-[18px] bg-white p-1.5 shadow-[0_4px_14px_rgba(0,0,0,0.08)] transition-all sm:h-[112px] sm:w-[112px] sm:rounded-[20px] sm:p-2 lg:h-[128px] lg:w-[128px] ${
                            index === currentImageIndex ? 'ring-2 ring-[#5d76cb]' : 'ring-1 ring-[#ebefff]'
                          }`}
                        >
                          <img src={thumb} alt={`Изображение ${index + 1}`} className="h-full w-full object-contain" onLoad={updateThumbnailsScrollState} />
                        </button>
                      ))}
                    </div>

                    {canScrollThumbnailsRight && (
                      <button
                        type="button"
                        onClick={() => scrollThumbs('right')}
                        className="absolute right-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 shadow-[0_4px_10px_rgba(0,0,0,0.12)]"
                        aria-label="Прокрутить миниатюры вправо"
                      >
                        <img src={arrowLeftIcon} alt="Вперёд" className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-8 overflow-hidden rounded-[24px] bg-white shadow-[inset_0_0_0_1px_rgba(93,118,203,0.08)]">
                <div className="aspect-[4/5] bg-white p-4 sm:p-5 lg:p-6">
                  <img src={additionalImage} alt="Дополнительное изображение" className="h-full w-full object-contain" />
                </div>
              </div>

              <div className="mt-8">
                <h2 className="mb-4 text-[28px] font-semibold text-black">Описание</h2>
                <p className="whitespace-pre-wrap text-[18px] leading-[1.6] text-black sm:text-[19px]">
                  {product.description || product.short_description || 'Описание товара отсутствует'}
                </p>
              </div>
            </section>

            <aside className="order-2 lg:sticky lg:top-5 lg:self-start">
              <div className="space-y-5">
                {userId ? (
                  <AddressBlock userId={userId} className="w-full" />
                ) : (
                  <div className="flex min-h-[72px] items-center rounded-[18px] border border-[#c8d4ff] bg-white px-4 py-4 text-[14px] leading-[20px] text-[#2f3d63] sm:min-h-[80px] sm:rounded-[20px] sm:px-[18px] sm:text-[16px] sm:leading-[22px]">
                    Чтобы добавить товар в корзину и оформить заказ, войдите или зарегистрируйтесь в аккаунте.
                  </div>
                )}

                <div className={`rounded-[24px] p-3 shadow-[0_8px_24px_rgba(0,0,0,0.05)] ${priceBadgeBgClass}`}>
                  <div className="relative rounded-[20px] bg-transparent px-4 py-5 sm:px-5 sm:py-6">
                    {discountPercent > 0 && <div className="absolute right-4 top-4 h-4 w-4 rounded-full bg-white/70" />}
                    <div className="text-[34px] font-bold leading-none tracking-[-0.5px] text-black sm:text-[42px] lg:text-[50px]">
                      {formatPrice(product.price)} ₽
                    </div>
                    {inferredOldPrice && (
                      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                        <span className="text-[20px] leading-none text-[#727272] line-through sm:text-[24px]">{formatPrice(inferredOldPrice)} ₽</span>
                        {discountPercent > 0 && <span className="text-[18px] font-semibold leading-none text-[#d7263d] sm:text-[20px]">-{discountPercent}%</span>}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 sm:gap-4">
                  {userId && cartQuantity > 0 ? (
                    <div className="flex h-[60px] min-w-0 flex-1 items-center justify-between rounded-[20px] border border-[#dbe3ff] bg-white px-[14px] shadow-[0_0_8px_rgba(93,118,203,0.15)]">
                      <button onClick={handleDecreaseCart} className="flex h-[36px] w-[36px] items-center justify-center rounded-[10px] transition-colors hover:bg-[#f1f4ff]" aria-label="Уменьшить количество">
                        <div className="h-[2px] w-[14px] rounded-[4px] bg-black" />
                      </button>
                      <span className="min-w-[24px] text-center text-[24px] font-semibold text-black">{cartQuantity}</span>
                      <button onClick={handleIncreaseCart} className="relative flex h-[36px] w-[36px] items-center justify-center rounded-[10px] transition-colors hover:bg-[#f1f4ff]" aria-label="Увеличить количество">
                        <div className="h-[2px] w-[14px] rounded-[4px] bg-black" />
                        <div className="absolute h-[14px] w-[2px] rounded-[4px] bg-black" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleAddToCart}
                      disabled={!userId}
                      className={`flex h-[54px] min-w-0 flex-1 items-center justify-center gap-3 rounded-[18px] transition-colors sm:h-[60px] sm:rounded-[20px] ${userId ? 'bg-[#5d76cb] hover:bg-[#4d66bb]' : 'cursor-not-allowed bg-[#b9bfcd]'}`}
                    >
                      <img src={cartIcon} alt="Корзина" className="h-[26px] w-[26px]" />
                      <span className="text-[17px] font-semibold tracking-[1px] text-white sm:text-[20px] sm:tracking-[1.4px]">В корзину</span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleToggleFavorite}
                    className="flex h-[54px] w-[54px] flex-shrink-0 items-center justify-center rounded-[18px] bg-white shadow-[0_6px_18px_rgba(0,0,0,0.08)] transition-shadow hover:shadow-[0_8px_22px_rgba(0,0,0,0.12)] sm:h-[60px] sm:w-[60px] sm:rounded-[20px]"
                  >
                    <img src={isFavorite ? addedToFavoritesIcon : favoritesBlueIcon} alt={isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'} className="h-[34px] w-[34px] sm:h-[40px] sm:w-[40px]" />
                  </button>
                </div>

                <div className="text-right text-[13px] text-[#727272] sm:text-[15px]">Артикул: {product.article || product.id}</div>

                <div>
                  <h3 className="mb-4 text-[26px] font-semibold text-black sm:text-[30px]">Продавец</h3>
                  <div className="rounded-[20px] bg-white p-5 shadow-[0_8px_24px_rgba(0,0,0,0.05)]">
                    <div className="flex items-start gap-4">
                      <div className="flex h-[96px] w-[96px] flex-shrink-0 items-center justify-center overflow-hidden rounded-[16px] bg-white shadow-[inset_0_0_0_1px_rgba(93,118,203,0.08)]">
                        <img src={sellerAvatar} alt={sellerTitle} className="h-full w-full object-contain p-1" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="truncate text-[24px] font-semibold leading-[1.1] text-black sm:text-[28px]">{sellerTitle}</h4>
                        <p className="mt-3 text-[14px] leading-[20px] text-[#727272]">{sellerDescription}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </main>
      )}
    </div>
  );
}





